import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Shield, KeyRound, RefreshCcw, Loader2, CheckCircle2, Eye, EyeOff, Wrench, ArrowLeft } from 'lucide-react';
import { SettingsLayout } from '@/components/settings/SettingsLayout';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { ModelDropdown } from '@/components/ui/ModelDropdown';
import { adminSettings, type SystemSetting, type ProviderModel } from '@/lib/api';
import { useUserStore } from '@/stores/userStore';
import '@/styles/admin-settings.css';

type Provider = 'openai' | 'anthropic';

const PROVIDER_LABELS: Record<Provider, { name: string; description: string; placeholder: string }> = {
  openai: {
    name: 'OpenAI',
    description: 'GPT-4o, GPT-4.1, and o-series reasoning models',
    placeholder: 'sk-...'
  },
  anthropic: {
    name: 'Anthropic',
    description: 'Claude 3/3.5 models',
    placeholder: 'sk-ant-...'
  }
};

interface ApiFormState {
  editing: boolean;
  input: string;
  saving: boolean;
  show: boolean;
  message?: { type: 'success' | 'error'; text: string };
}

const initialApiFormState: Record<Provider, ApiFormState> = {
  openai: { editing: false, input: '', saving: false, show: false },
  anthropic: { editing: false, input: '', saving: false, show: false }
};

export function AdminSettingsPage() {
  const navigate = useNavigate();
  const { currentUser } = useUserStore();
  const isAdmin = currentUser?.role === 'admin';

  const [settings, setSettings] = useState<SystemSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [defaultProvider, setDefaultProvider] = useState<Provider>('openai');
  const [defaultModel, setDefaultModel] = useState('');
  const [openaiModels, setOpenaiModels] = useState<ProviderModel[]>([]);
  const [anthropicModels, setAnthropicModels] = useState<ProviderModel[]>([]);
  const [modelLoading, setModelLoading] = useState<Record<Provider, boolean>>({ openai: false, anthropic: false });
  const [modelMessages, setModelMessages] = useState<Record<Provider, string>>({ openai: '', anthropic: '' });
  const [providerSaving, setProviderSaving] = useState(false);
  const [modelSaving, setModelSaving] = useState(false);
  const [apiForms, setApiForms] = useState(initialApiFormState);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [showValue, setShowValue] = useState<Record<string, boolean>>({});
  const [saveStatus, setSaveStatus] = useState<Record<string, 'saving' | 'saved' | 'error'>>({});

  const advancedSettings = useMemo(() => {
    const skipKeys = new Set(['openai_api_key', 'anthropic_api_key', 'default_ai_provider', 'default_model']);
    return settings.filter((setting) => !skipKeys.has(setting.key));
  }, [settings]);

  const settingsMap = useMemo(() => {
    const map = new Map<string, SystemSetting>();
    settings.forEach((setting) => map.set(setting.key, setting));
    return map;
  }, [settings]);

  const handleLoad = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminSettings.getAll();
      setSettings(data.settings);

      const providerSetting = data.settings.find((s) => s.key === 'default_ai_provider');
      const modelSetting = data.settings.find((s) => s.key === 'default_model');
      setDefaultProvider((providerSetting?.value as Provider) || 'openai');
      setDefaultModel(modelSetting?.value || '');

      const openaiConfigured = Boolean(data.settings.find((s) => s.key === 'openai_api_key')?.value);
      if (!openaiConfigured) {
        setOpenaiModels([]);
        setModelMessages((prev) => ({
          ...prev,
          openai: 'Save an OpenAI API key, then click “Refresh models”.'
        }));
      } else {
        setModelMessages((prev) => ({
          ...prev,
          openai: 'Click “Refresh models” to load the latest list from OpenAI.'
        }));
      }

      // Anthropic uses a static catalog so we can load immediately
      fetchModels('anthropic');
    } catch (err) {
      console.error('Failed to load settings:', err);
      setError('Failed to load settings. Please ensure you have admin access.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleLoad();
  }, []);

  const updateApiForm = (provider: Provider, patch: Partial<ApiFormState>) => {
    setApiForms((prev) => ({
      ...prev,
      [provider]: { ...prev[provider], ...patch }
    }));
  };

  const fetchModels = async (provider: Provider, options?: { apiKey?: string }) => {
    setModelLoading((prev) => ({ ...prev, [provider]: true }));
    setModelMessages((prev) => ({ ...prev, [provider]: '' }));
    try {
      const models = await adminSettings.fetchModels(provider, options);
      if (provider === 'openai') {
        setOpenaiModels(models);
      } else {
        setAnthropicModels(models);
      }
      setModelMessages((prev) => ({
        ...prev,
        [provider]: `Loaded ${models.length} models${options?.apiKey ? ' using provided key' : ''}.`
      }));
    } catch (err) {
      console.error(`Failed to fetch ${provider} models`, err);
      const message =
        (err as any)?.response?.data?.error ||
        (err instanceof Error ? err.message : 'Failed to fetch models.');
      const friendly = /ENOTFOUND|Failed to fetch|network/i.test(message)
        ? 'Model catalog unavailable (likely offline). Enter a model ID manually or retry later.'
        : message;
      setModelMessages((prev) => ({
        ...prev,
        [provider]: friendly
      }));
    } finally {
      setModelLoading((prev) => ({ ...prev, [provider]: false }));
    }
  };

  const handleSaveProvider = async (provider: Provider) => {
    try {
      setProviderSaving(true);
      await adminSettings.update('default_ai_provider', provider);
      setDefaultProvider(provider);
    } catch (err) {
      console.error('Failed to update provider', err);
    } finally {
      setProviderSaving(false);
    }
  };

  const handleSelectModel = async (modelId: string) => {
    try {
      setModelSaving(true);
      await adminSettings.update('default_model', modelId);
      setDefaultModel(modelId);
    } catch (err) {
      console.error('Failed to update default model', err);
    } finally {
      setModelSaving(false);
    }
  };

  const handleSaveApiKey = async (provider: Provider) => {
    const { input } = apiForms[provider];
    if (!input.trim()) {
      updateApiForm(provider, { message: { type: 'error', text: 'API key is required.' } });
      return;
    }

    try {
      updateApiForm(provider, { saving: true });
      await adminSettings.update(`${provider}_api_key`, input.trim());
      updateApiForm(provider, {
        editing: false,
        input: '',
        show: false,
        saving: false,
        message: { type: 'success', text: 'API key saved and encrypted.' }
      });
      await handleLoad();
    } catch (err) {
      console.error(`Failed to save ${provider} key`, err);
      updateApiForm(provider, {
        saving: false,
        message: { type: 'error', text: 'Failed to store API key. Check logs for details.' }
      });
    }
  };

  const handleClearApiKey = async (provider: Provider) => {
    try {
      await adminSettings.clear(`${provider}_api_key`);
      updateApiForm(provider, {
        message: { type: 'success', text: 'API key removed.' }
      });
      await handleLoad();
    } catch (err) {
      console.error(`Failed to clear ${provider} key`, err);
      updateApiForm(provider, {
        message: { type: 'error', text: 'Failed to clear API key.' }
      });
    }
  };

  const handleEditSetting = (setting: SystemSetting) => {
    setEditingKey(setting.key);
    setEditValue(setting.value || '');
  };

  const handleCancelSetting = () => {
    setEditingKey(null);
    setEditValue('');
  };

  const handleSaveSetting = async (key: string) => {
    try {
      setSaveStatus((prev) => ({ ...prev, [key]: 'saving' }));
      await adminSettings.update(key, editValue);
      setSaveStatus((prev) => ({ ...prev, [key]: 'saved' }));
      setTimeout(() => {
        setSaveStatus((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }, 2000);
      setEditingKey(null);
      setEditValue('');
      await handleLoad();
    } catch (err) {
      console.error('Failed to save setting', err);
      setSaveStatus((prev) => ({ ...prev, [key]: 'error' }));
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8">
        <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-600 text-center max-w-md">
          You need administrator privileges to access system settings. Please contact an administrator if you need access.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const primaryBtn = 'primaryBtn bg-rose-600 text-white hover:bg-rose-500 shadow-sm';
  const outlineBtn = 'outlineBtn border border-slate-500 text-slate-100 hover:border-slate-300 hover:text-white';
  const ghostBtn = 'ghostBtn text-slate-200 hover:text-white';
  const dangerGhostBtn = 'dangerGhostBtn text-rose-300 hover:text-rose-200';

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8">
        <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Error</h2>
        <p className="text-gray-600 text-center max-w-md mb-4">{error}</p>
        <Button onClick={handleLoad} className={primaryBtn}>
          Retry
        </Button>
      </div>
    );
  }

  const openaiSetting = settingsMap.get('openai_api_key');
  const anthropicSetting = settingsMap.get('anthropic_api_key');
  const selectedModels = defaultProvider === 'openai' ? openaiModels : anthropicModels;

  return (
    <SettingsLayout
      variant="admin"
      icon={<Shield className="w-7 h-7" />}
      title="Admin Settings"
      subtitle="Configure encrypted API keys, choose the default provider, and fine-tune system options for the entire factory."
      badge="Admin"
      actions={
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            size="sm"
            className={cn('w-40', outlineBtn)}
            onClick={() => navigate('/settings')}
          >
            Workspace Settings
          </Button>
          <Button
            variant="outline"
            size="sm"
            className={cn('w-40 backBtn')}
            onClick={() => navigate('/')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Builder
          </Button>
        </div>
      }
    >
      <div className="admin-page space-y-8">
      {/* Provider and default model */}
      <section className="bg-white border border-rose-100 rounded-xl shadow-sm p-6 space-y-5">
        <div className="flex items-center gap-3">
          <Wrench className="w-5 h-5 text-rose-500" />
          <div>
            <h3 className="text-lg font-semibold text-foreground">Active provider & default model</h3>
            <p className="text-sm text-muted-foreground">
              Pick which AI stack backs generation and which model the queue uses when a project does not override it.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          {(['openai', 'anthropic'] as Provider[]).map((provider) => {
            const isActive = defaultProvider === provider;
            return (
              <button
                key={provider}
                onClick={() => handleSaveProvider(provider)}
                className={cn(
                  'px-4 py-2 rounded-lg border text-sm font-semibold transition-colors',
                  isActive
                    ? 'bg-rose-600 border-rose-500 text-white shadow-sm hover:bg-rose-500'
                    : 'bg-slate-800 border-slate-600 text-slate-100 hover:border-slate-400'
                )}
                disabled={providerSaving}
              >
                {PROVIDER_LABELS[provider].name}
              </button>
            );
          })}
        </div>

        <div className="border border-slate-200 dark:border-border rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-900 dark:text-foreground">Default model</p>
              <p className="text-xs text-slate-500 dark:text-muted-foreground">
                Shows models available for the active provider. Refresh the catalog after updating API keys.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fetchModels(defaultProvider)}
                disabled={modelLoading[defaultProvider]}
                className={ghostBtn}
              >
                {modelLoading[defaultProvider] ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-1" />}
                Refresh list
              </Button>
            </div>
          </div>
          {selectedModels.length > 0 ? (
            <ModelDropdown
              models={selectedModels}
              value={defaultModel}
              onChange={handleSelectModel}
              disabled={modelSaving}
              placeholder="Select a model"
            />
          ) : (
            <div className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-3">
              No models available yet. Save an API key and refresh the list.
            </div>
          )}
          {modelMessages[defaultProvider] && (
            <p className="text-xs text-slate-500 dark:text-muted-foreground">{modelMessages[defaultProvider]}</p>
          )}
        </div>
      </section>

      {/* API Keys */}
      <section className="grid gap-6 lg:grid-cols-2">
        {(['openai', 'anthropic'] as Provider[]).map((provider) => {
          const form = apiForms[provider];
          const setting = provider === 'openai' ? openaiSetting : anthropicSetting;
          const hasKey = Boolean(setting?.value);
          return (
            <div key={provider} className="bg-white dark:bg-card border border-slate-200 dark:border-border rounded-xl shadow-sm p-6 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-foreground">{PROVIDER_LABELS[provider].name} API key</p>
                  <p className="text-xs text-slate-500 dark:text-muted-foreground">{PROVIDER_LABELS[provider].description}</p>
                </div>
                <KeyRound className="w-5 h-5 text-slate-400 dark:text-muted-foreground" />
              </div>

              {hasKey && !form.editing && (
                <code className="block text-sm font-mono bg-slate-50 dark:bg-muted/40 border border-slate-100 dark:border-border rounded-lg px-3 py-2 text-foreground">
                  {setting?.value || '••••••••'}
                </code>
              )}

              {form.editing ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <input
                      type={form.show ? 'text' : 'password'}
                      value={form.input}
                      onChange={(e) => updateApiForm(provider, { input: e.target.value })}
                      className="flex-1 border border-slate-300 dark:border-border rounded-lg px-3 py-2 text-sm bg-white dark:bg-background text-slate-900 dark:text-foreground"
                      placeholder={PROVIDER_LABELS[provider].placeholder}
                      autoFocus
                    />
                    <button
                      className="p-2 text-slate-500 dark:text-muted-foreground hover:text-slate-700 dark:hover:text-foreground"
                      title={form.show ? 'Hide value' : 'Show value'}
                      onClick={() => updateApiForm(provider, { show: !form.show })}
                      type="button"
                    >
                      {form.show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={() => handleSaveApiKey(provider)}
                      disabled={form.saving}
                      className={primaryBtn}
                    >
                      {form.saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Save key
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        updateApiForm(provider, { editing: false, input: '', show: false, message: undefined });
                      }}
                      disabled={form.saving}
                      className={outlineBtn}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => fetchModels(provider, { apiKey: form.input })}
                      disabled={!form.input || modelLoading[provider]}
                      className={ghostBtn}
                    >
                      {modelLoading[provider] ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
                      Test & fetch models
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-3">
                  <Button
                    variant="outline"
                    onClick={() => updateApiForm(provider, { editing: true, input: '', message: undefined })}
                    className={outlineBtn}
                  >
                    Update key
                  </Button>
                  {hasKey && (
                    <>
                      <Button
                        variant="ghost"
                        onClick={() => fetchModels(provider)}
                        disabled={modelLoading[provider]}
                        className={ghostBtn}
                      >
                        {modelLoading[provider] ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
                        Refresh models
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => handleClearApiKey(provider)}
                        className={dangerGhostBtn}
                      >
                        Remove key
                      </Button>
                    </>
                  )}
                </div>
              )}

              {form.message && (
                <div className={cn('text-xs rounded-md px-3 py-2', form.message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-rose-50 text-rose-700')}>
                  {form.message.text}
                </div>
              )}
              {modelMessages[provider] && !form.editing && (
                <p className="text-xs text-slate-500 dark:text-muted-foreground">{modelMessages[provider]}</p>
              )}
            </div>
          );
        })}
      </section>

      {/* Advanced settings */}
      <section className="bg-white dark:bg-card border border-slate-200 dark:border-border rounded-xl shadow-sm p-6 space-y-4">
        <div className="flex items-center gap-3">
          <Shield className="w-5 h-5 text-rose-500" />
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-foreground">Advanced system values</h3>
            <p className="text-sm text-slate-600 dark:text-muted-foreground">
              Most teams can ignore this section. Values here power logging, auditing, and experimental features.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          {advancedSettings.length === 0 && (
            <p className="text-sm text-slate-600 dark:text-muted-foreground">No additional system settings.</p>
          )}
          {advancedSettings.map((setting) => {
            const isEditing = editingKey === setting.key;
            const status = saveStatus[setting.key];
            return (
              <div key={setting.key} className="border border-slate-200 dark:border-border rounded-lg p-4 flex flex-col gap-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-foreground">
                      {setting.key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-muted-foreground">{setting.description}</p>
                  </div>
                  {setting.isSensitive && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 font-semibold">Sensitive</span>
                  )}
                </div>
                {isEditing ? (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                      <input
                        type={setting.isSensitive && !showValue[setting.key] ? 'password' : 'text'}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="flex-1 border border-slate-300 dark:border-border rounded-lg px-3 py-2 text-sm bg-white dark:bg-background text-slate-900 dark:text-foreground"
                        autoFocus
                      />
                      {setting.isSensitive && (
                        <button
                          className="p-2 text-slate-500 dark:text-muted-foreground hover:text-slate-700 dark:hover:text-foreground"
                          onClick={() => setShowValue((prev) => ({ ...prev, [setting.key]: !prev[setting.key] }))}
                        >
                          {showValue[setting.key] ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      )}
                    </div>
                    <div className="flex gap-3">
                      <Button onClick={() => handleSaveSetting(setting.key)} disabled={status === 'saving'} className={primaryBtn}>
                        {status === 'saving' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Save
                      </Button>
                      <Button variant="outline" onClick={handleCancelSetting} disabled={status === 'saving'} className={outlineBtn}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <code className="text-sm font-mono bg-slate-50 dark:bg-muted/40 border border-slate-100 dark:border-border rounded px-3 py-2 text-slate-700 dark:text-foreground flex-1">
                      {setting.value ?? '(not set)'}
                    </code>
                    <Button variant="ghost" onClick={() => handleEditSetting(setting)} size="sm" className={ghostBtn}>
                      Edit
                    </Button>
                  </div>
                )}
                {status === 'saved' && (
                  <div className="flex items-center gap-2 text-xs text-green-600">
                    <CheckCircle2 className="w-4 h-4" /> Saved
                  </div>
                )}
                {status === 'error' && (
                  <div className="flex items-center gap-2 text-xs text-rose-600">
                    <AlertCircle className="w-4 h-4" /> Failed to save
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-6 p-4 bg-slate-50 dark:bg-muted/40 border border-slate-100 dark:border-border rounded-lg flex items-start gap-3">
          <Shield className="w-5 h-5 text-muted-foreground mt-0.5" />
          <div className="text-sm text-foreground space-y-1">
            <p>Security reminders:</p>
            <ul className="list-disc pl-4 space-y-1">
              <li>API keys are encrypted before storage.</li>
              <li>Sensitive values are masked whenever possible.</li>
              <li>All changes are logged with user attribution.</li>
              <li>Share access sparingly—this page affects every workspace user.</li>
            </ul>
          </div>
        </div>
      </section>
      </div>
    </SettingsLayout>
  );
}
